import type { MyBotDto } from '@sportspredict/shared';
import { LeaderboardScoringMode } from '@sportspredict/shared';
import { BOT_NAME_MAX, getBotDisplayName } from './bot-display.utils';

export type HomeRbpDisplayMode = 'user' | 'single-bot' | 'best-bot';

export interface HomeRbpDisplay {
	mode: HomeRbpDisplayMode;
	/** Label above the score, e.g. "TOTAL RBP", "ClaudeBOT's RBP", "Your Best Bot's RBP" */
	title: string;
	score: number | null | undefined;
	/** Bot that owns the displayed score when mode is not `user` */
	bot: MyBotDto | null;
	/** Navigate CTA to the dedicated bot dashboard */
	showDashboardCta: boolean;
	scoringMode: LeaderboardScoringMode | null;
}

function getBotScore(bot: MyBotDto): number {
	return Number(bot.score ?? 0);
}

function getBotRank(bot: MyBotDto): number {
	const rank = bot.rank;
	if (rank == null || rank <= 0) return Number.POSITIVE_INFINITY;
	return rank;
}

/** Possessive form for bot label: "ClaudeBOT" → "ClaudeBOT's", "James" → "James'" */
export function formatBotPossessiveName(label: string | null | undefined): string {
	const name = label?.trim() || 'Bot';
	return name.endsWith('s') || name.endsWith('S') ? `${name}'` : `${name}'s`;
}

export function getTruncatedBotPossessiveName(label: string | null | undefined): string {
	const { display } = getBotDisplayName(label, BOT_NAME_MAX.homeRbpTitle, 'Bot');
	return formatBotPossessiveName(display);
}

/**
 * Pick the bot with the highest RBP. Tie-break: better (lower) rank, then stable id.
 */
export function getBestBotByRbp(bots: MyBotDto[]): MyBotDto | null {
	if (!bots.length) return null;

	return [...bots].sort((a, b) => {
		const scoreDiff = getBotScore(b) - getBotScore(a);
		if (scoreDiff !== 0) return scoreDiff;

		const rankDiff = getBotRank(a) - getBotRank(b);
		if (rankDiff !== 0) return rankDiff;

		return a.id.localeCompare(b.id);
	})[0];
}

/**
 * Home RBP card display rules (Figma Dedicated Bot Dashboard entry points):
 * - 1 bot and bot RBP > user RBP → "{BotName}'s RBP" + CTA to bot dashboard
 * - 2+ bots and best bot RBP > user RBP → "Your Best Bot's RBP" + CTA
 * - otherwise → user's Total RBP (no bot CTA)
 */
export function resolveHomeRbpDisplay(
	userScore: number | null | undefined,
	bots: MyBotDto[] | undefined,
	userScoreReady = true,
	scoringMode: LeaderboardScoringMode | null = null
): HomeRbpDisplay {
	const rbpTitle = scoringMode === LeaderboardScoringMode.Average ? 'AVERAGE RBP' : 'TOTAL RBP';
	if (!userScoreReady) {
		return {
			mode: 'user',
			title: rbpTitle,
			score: userScore,
			bot: null,
			showDashboardCta: false,
			scoringMode,
		};
	}

	const userRbp = Number(userScore ?? 0);
	const list = (bots ?? []).filter((bot) => bot.score != null);

	if (list.length === 0) {
		return {
			mode: 'user',
			title: rbpTitle,
			score: userScore,
			bot: null,
			showDashboardCta: false,
			scoringMode,
		};
	}

	if (list.length === 1) {
		const bot = list[0];
		if (getBotScore(bot) > userRbp) {
			const { display } = getBotDisplayName(bot.label, BOT_NAME_MAX.homeRbpTitle, 'Bot');
			return {
				mode: 'single-bot',
				title: `${formatBotPossessiveName(display)} RBP`,
				score: bot.score,
				bot,
				showDashboardCta: true,
				scoringMode,
			};
		}
		return {
			mode: 'user',
			title: rbpTitle,
			score: userScore,
			bot: null,
			showDashboardCta: false,
			scoringMode,
		};
	}

	const bestBotTitle =
		scoringMode === LeaderboardScoringMode.Average
			? "Your Best Bot's Average RBP"
			: "Your Best Bot's Total RBP";

	const bestBot = getBestBotByRbp(list);
	if (bestBot && getBotScore(bestBot) > userRbp) {
		return {
			mode: 'best-bot',
			title: bestBotTitle,
			score: bestBot.score,
			bot: bestBot,
			showDashboardCta: true,
			scoringMode,
		};
	}

	return {
		mode: 'user',
		title: rbpTitle,
		score: userScore,
		bot: null,
		showDashboardCta: false,
		scoringMode,
	};
}
